import { useMemo } from 'react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * Patriotic loading animation for the State Comparison page.
 * AHP: red/white/blue stripes with gold stars and waving flag effect
 * Luigi's: matrix-green data-stream with state silhouettes
 */
interface PatrioticLoaderProps {
  message?: string;
  subMessage?: string;
}

export const PatrioticLoader = ({ message, subMessage }: PatrioticLoaderProps = {}) => {
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';

  // Generate star positions for the canton (blue field)
  const stars = useMemo(() => {
    const positions: { x: number; y: number; delay: number }[] = [];
    // 5 rows of 6, 4 rows of 5 = 50 stars (simplified grid)
    for (let row = 0; row < 9; row++) {
      const cols = row % 2 === 0 ? 6 : 5;
      const offsetX = row % 2 === 0 ? 0 : 8;
      for (let col = 0; col < cols; col++) {
        positions.push({
          x: offsetX + col * 16,
          y: 4 + row * 10,
          delay: Math.random() * 2,
        });
      }
    }
    return positions;
  }, []);

  if (!isAhp) {
    // Luigi's brand: matrix-style loading
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-6">
        <div className="relative w-48 h-32">
          {/* Scanning bar */}
          <div
            className="absolute inset-x-0 h-0.5 rounded-full"
            style={{
              background: '#00ff41',
              boxShadow: '0 0 12px #00ff41, 0 0 24px rgba(0,255,65,0.3)',
              animation: 'matrixScan 2s ease-in-out infinite',
            }}
          />
          {/* Grid of state dots */}
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                width: 4,
                height: 4,
                left: `${(i % 10) * 10 + 5}%`,
                top: `${Math.floor(i / 10) * 20 + 10}%`,
                backgroundColor: '#00ff41',
                opacity: 0,
                animation: `matrixDotFade 2.5s ease-in-out ${i * 0.04}s infinite`,
              }}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span
            className="font-mono text-sm tracking-wider"
            style={{
              color: '#00ff41',
              textShadow: '0 0 8px rgba(0,255,65,0.5)',
              animation: 'matrixTextPulse 1.5s ease-in-out infinite',
            }}
          >
            LOADING STATE DATA
          </span>
          <span className="font-mono text-sm" style={{ color: '#00ff41' }}>
            <span style={{ animation: 'matrixDotFade 1s ease-in-out 0s infinite' }}>.</span>
            <span style={{ animation: 'matrixDotFade 1s ease-in-out 0.2s infinite' }}>.</span>
            <span style={{ animation: 'matrixDotFade 1s ease-in-out 0.4s infinite' }}>.</span>
          </span>
        </div>
        <style>{`
          @keyframes matrixScan {
            0%, 100% { top: 0%; opacity: 0.3; }
            50% { top: 90%; opacity: 1; }
          }
          @keyframes matrixDotFade {
            0%, 100% { opacity: 0.1; transform: scale(0.5); }
            50% { opacity: 1; transform: scale(1.2); }
          }
          @keyframes matrixTextPulse {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  // AHP patriotic loading animation
  const bgColor = isDark ? 'var(--civic-bg-dark)' : 'var(--civic-cream)';
  const navy = isDark ? 'var(--civic-navy-deep)' : 'var(--civic-navy)';
  const red = 'var(--civic-red)';
  const white = isDark ? 'var(--civic-cream)' : '#FFFFFF';
  const gold = 'var(--civic-gold)';
  const textCol = isDark ? 'var(--civic-cream)' : 'var(--civic-ink)';
  const mutedCol = isDark ? 'rgba(var(--civic-cream-rgb),0.5)' : 'rgba(0,0,0,0.4)';

  return (
    <div className="flex flex-col items-center justify-center py-16 gap-8">
      {/* Flag container with wave animation */}
      <div
        className="relative overflow-hidden rounded-lg shadow-xl"
        style={{
          width: 240,
          height: 140,
          animation: 'flagFloat 3s ease-in-out infinite',
        }}
      >
        {/* Stripes */}
        {Array.from({ length: 13 }).map((_, i) => (
          <div
            key={`stripe-${i}`}
            className="absolute left-0 right-0"
            style={{
              top: `${(i / 13) * 100}%`,
              height: `${100 / 13}%`,
              backgroundColor: i % 2 === 0 ? red : white,
              animation: `stripeWave 2.5s ease-in-out ${i * 0.08}s infinite`,
              transformOrigin: 'left center',
            }}
          />
        ))}

        {/* Canton (blue field) */}
        <div
          className="absolute top-0 left-0 z-10"
          style={{
            width: '42%',
            height: `${(7 / 13) * 100}%`,
            backgroundColor: navy,
            animation: 'stripeWave 2.5s ease-in-out 0s infinite',
            transformOrigin: 'left center',
          }}
        >
          {/* Stars */}
          <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
            {stars.map((star, i) => (
              <text
                key={i}
                x={star.x + 2}
                y={star.y + 6}
                fill={gold}
                fontSize="6"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  opacity: 0,
                  animation: `starTwinkle 2s ease-in-out ${star.delay}s infinite`,
                }}
              >
                ★
              </text>
            ))}
          </svg>
        </div>

        {/* Shimmer overlay */}
        <div
          className="absolute inset-0 z-20 pointer-events-none"
          style={{
            background: `linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)`,
            animation: 'flagShimmer 3s ease-in-out infinite',
          }}
        />

        {/* Progress bar at bottom */}
        <div
          className="absolute bottom-0 left-0 z-30 h-1"
          style={{
            backgroundColor: gold,
            animation: 'progressSweep 2.5s ease-in-out infinite',
            boxShadow: `0 0 8px ${gold}`,
          }}
        />
      </div>

      {/* Text below flag */}
      <div className="flex flex-col items-center gap-2">
        <p
          className="text-base font-medium tracking-wide"
          style={{
            color: textCol,
            fontFamily: "'Instrument Serif', Georgia, serif",
            animation: 'textFadeIn 1.5s ease-in-out infinite alternate',
          }}
        >
          {message ?? 'Comparing All 50 States'}
        </p>
        <div className="flex items-center gap-1.5">
          {/* Animated dots with patriotic colors */}
          {[red, white, navy, gold, red].map((color, i) => (
            <div
              key={i}
              className="rounded-full"
              style={{
                width: 6,
                height: 6,
                backgroundColor: color,
                border: color === white ? `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}` : 'none',
                animation: `dotBounce 1.2s ease-in-out ${i * 0.15}s infinite`,
              }}
            />
          ))}
        </div>
        <p
          className="text-xs"
          style={{ color: mutedCol, fontFamily: "'Instrument Sans', sans-serif" }}
        >
          {subMessage ?? 'Loading healthcare pricing data'}
        </p>
      </div>

      <style>{`
        @keyframes flagFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @keyframes stripeWave {
          0%, 100% { transform: scaleX(1) skewY(0deg); }
          25% { transform: scaleX(1.01) skewY(0.5deg); }
          75% { transform: scaleX(0.99) skewY(-0.5deg); }
        }
        @keyframes starTwinkle {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes flagShimmer {
          0% { transform: translateX(-120%); }
          60%, 100% { transform: translateX(120%); }
        }
        @keyframes progressSweep {
          0% { width: 0%; opacity: 0.8; }
          50% { width: 100%; opacity: 1; }
          100% { width: 100%; opacity: 0; }
        }
        @keyframes dotBounce {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.4; }
          50% { transform: translateY(-8px) scale(1.3); opacity: 1; }
        }
        @keyframes textFadeIn {
          0% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};
